// src/utils/universalUploadHelper.ts - Universal Upload Helper cho tất cả tables
import axios from 'axios';
import { getDocumentId, getStrapiErrorMessage } from './strapiV5Utils';

// ===== INTERFACES =====
export interface UniversalUploadOptions {
    schema: string; // Tên schema/table (content, document-table, teacher-document, etc.)
    fieldName: string; // Tên field trong schema (files, file, coverImage, etc.)
    documentId?: string | number; // ID của document đã tạo (nếu có)
    username?: string; // Username của user hiện tại
    customPathStructure?: string; // Custom path structure
    generateCustomPath?: boolean; // Có tạo custom path không
    metadata?: Record<string, any>; // Metadata bổ sung
}

export interface UploadResponse {
    success: boolean;
    data?: any;
    files?: UploadedFileInfo[];
    metadata?: {
        bucket: string;
        totalSize: number;
        uploadDate: string;
        storageProvider: 'minio';
        pathStructure: string;
        schema: string;
        fieldName: string;
        documentId?: string | number;
    };
    error?: string;
}

export interface UploadedFileInfo {
    id: number;
    name: string;
    originalName: string;
    url: string;
    size: number;
    mime: string;
    hash: string;
    path: string;
    bucket: string;
    customPath?: string;
    pathComponents?: PathComponents;
    uploadDate: string;
    fieldName: string;
    schema: string;
}

export interface PathComponents {
    schema: string;
    username: string;
    fieldName: string;
    year: string;
    month: string;
    day: string;
    documentId: string;
    filename: string;
}

// ===== CONSTANTS =====
const DEFAULT_PATH_STRUCTURE = '{schema}/{username}/{fieldName}/{year}/{month}/{day}/{documentId}/{hash}.{ext}';
const FALLBACK_USERNAME = 'anonymous';
const DEFAULT_BUCKET = 'vietrro-portal';

// ===== MAIN UPLOAD FUNCTION =====

/**
 * ✅ Universal upload function cho tất cả các table trong hệ thống
 * Hỗ trợ upload file cho bất kỳ schema nào với path structure linh hoạt
 */
export const universalUploadToStrapi = async (
    baseURL: string,
    token: string | null,
    files: File[],
    options: UniversalUploadOptions,
    onProgress?: (progress: number) => void
): Promise<UploadResponse> => {
    try {
        console.log(`🚀 Universal upload started for schema: ${options.schema}, field: ${options.fieldName}`);

        // ✅ Validate inputs
        if (!files || files.length === 0) {
            throw new Error('No files provided for upload');
        }

        if (!options.schema || !options.fieldName) {
            throw new Error('Schema and fieldName are required');
        }

        // ✅ Get current user info if not provided
        const username = options.username || await getCurrentUsername(baseURL, token);

        // ✅ Upload files
        const uploadedFiles = await uploadFilesWithUniversalPath(
            baseURL,
            token,
            files,
            {
                ...options,
                username,
                generateCustomPath: options.generateCustomPath !== false
            },
            onProgress
        );

        console.log(`✅ Successfully uploaded ${uploadedFiles.length} files for ${options.schema}.${options.fieldName}`);

        // ✅ Return comprehensive response
        return {
            success: true,
            files: uploadedFiles,
            metadata: {
                bucket: DEFAULT_BUCKET,
                totalSize: uploadedFiles.reduce((sum, file) => sum + file.size, 0),
                uploadDate: new Date().toISOString(),
                storageProvider: 'minio',
                pathStructure: options.customPathStructure || DEFAULT_PATH_STRUCTURE,
                schema: options.schema,
                fieldName: options.fieldName,
                documentId: options.documentId
            }
        };

    } catch (error) {
        console.error('❌ Universal upload failed:', error);
        return {
            success: false,
            error: getStrapiErrorMessage(error)
        };
    }
};

/**
 * ✅ Upload files và tự động link với record đã tồn tại
 */
export const uploadAndLinkToRecord = async (
    baseURL: string,
    token: string | null,
    files: File[],
    options: UniversalUploadOptions & { recordId: string | number },
    onProgress?: (progress: number) => void
): Promise<UploadResponse> => {
    try {
        console.log(`🔗 Upload and link files to ${options.schema} record ID: ${options.recordId}`);

        // ✅ Upload files
        const uploadResult = await universalUploadToStrapi(
            baseURL,
            token,
            files,
            {
                ...options,
                documentId: options.recordId
            },
            onProgress
        );

        if (!uploadResult.success || !uploadResult.files) {
            return uploadResult;
        }

        // ✅ Link files to existing record
        await linkFilesToRecord(
            baseURL,
            token,
            options.schema,
            options.recordId,
            options.fieldName,
            uploadResult.files
        );

        console.log(`✅ Files successfully linked to ${options.schema} record`);
        return uploadResult;

    } catch (error) {
        console.error('❌ Upload and link failed:', error);
        return {
            success: false,
            error: getStrapiErrorMessage(error)
        };
    }
};

/**
 * ✅ Create record và upload files cùng lúc
 */
export const createRecordWithFiles = async (
    baseURL: string,
    token: string | null,
    recordData: any,
    files: File[],
    options: UniversalUploadOptions,
    onProgress?: (progress: number) => void
): Promise<UploadResponse> => {
    try {
        console.log(`📝 Create ${options.schema} record with files`);

        // ✅ Step 1: Create record
        const record = await createRecord(baseURL, token, options.schema, recordData);

        if (!record?.id && !record?.documentId) {
            throw new Error('Failed to create record - no ID returned');
        }

        const recordId = record.documentId || record.id; // Strapi v5 compatibility
        console.log(`✅ Record created with ID: ${recordId}`);

        // ✅ Step 2: Upload files
        const uploadResult = await uploadAndLinkToRecord(
            baseURL,
            token,
            files,
            {
                ...options,
                recordId,
                documentId: recordId
            },
            onProgress
        );

        if (uploadResult.success) {
            uploadResult.data = record;
        }

        return uploadResult;

    } catch (error) {
        console.error('❌ Create record with files failed:', error);
        return {
            success: false,
            error: getStrapiErrorMessage(error)
        };
    }
};

// ===== CORE UPLOAD FUNCTIONS =====

/**
 * ✅ Upload files với universal path structure
 */
async function uploadFilesWithUniversalPath(
    baseURL: string,
    token: string | null,
    files: File[],
    options: UniversalUploadOptions & { username: string },
    onProgress?: (progress: number) => void
): Promise<UploadedFileInfo[]> {

    const uploadedFiles: UploadedFileInfo[] = [];

    for (let i = 0; i < files.length; i++) {
        const file = files[i];
        console.log(`📤 Uploading file ${i + 1}/${files.length}: ${file.name}`);

        // ✅ Prepare form data
        const formData = new FormData();
        formData.append('files', file);

        // ✅ Add reference information cho Strapi
        if (options.documentId) {
            formData.append('ref', `api::${options.schema}.${options.schema}`);
            formData.append('refId', options.documentId.toString());
            formData.append('field', options.fieldName);
        }

        // ✅ Add custom path generation parameters
        if (options.generateCustomPath) {
            formData.append('schema', options.schema);
            formData.append('fieldName', options.fieldName);
            formData.append('username', options.username);
            formData.append('generateCustomPath', 'true');

            if (options.documentId) {
                formData.append('documentId', options.documentId.toString());
            }

            if (options.customPathStructure) {
                formData.append('pathStructure', options.customPathStructure);
            }
        }

        // ✅ Add metadata
        if (options.metadata) {
            formData.append('metadata', JSON.stringify(options.metadata));
        }

        try {
            // ✅ Upload file
            const uploadResponse = await axios({
                method: 'POST',
                url: `${baseURL}/api/upload`,
                data: formData,
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'Authorization': token ? `Bearer ${token}` : '',
                },
                onUploadProgress: (progressEvent) => {
                    if (onProgress && progressEvent.total) {
                        const fileProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        const totalProgress = Math.round(((i * 100) + fileProgress) / files.length);
                        onProgress(totalProgress);
                    }
                }
            });

            // ✅ Process response
            if (uploadResponse.data && Array.isArray(uploadResponse.data)) {
                const uploadedFile = uploadResponse.data[0];

                // ✅ Generate custom path info
                const customPathInfo = parseUniversalMinioPath(uploadedFile.url);
                const minioPath = extractMinioPath(uploadedFile.url);

                const fileInfo: UploadedFileInfo = {
                    id: uploadedFile.id,
                    name: uploadedFile.name,
                    originalName: file.name,
                    url: uploadedFile.url,
                    size: uploadedFile.size,
                    mime: uploadedFile.mime,
                    hash: uploadedFile.hash,
                    path: minioPath,
                    bucket: extractBucketFromUrl(uploadedFile.url),
                    customPath: minioPath,
                    pathComponents: customPathInfo,
                    uploadDate: new Date().toISOString(),
                    fieldName: options.fieldName,
                    schema: options.schema
                };

                uploadedFiles.push(fileInfo);

                console.log(`✅ File uploaded: ${file.name}`);
                console.log(`   📂 Path: ${minioPath}`);
                if (customPathInfo) {
                    console.log(`   📂 Structure: ${customPathInfo.schema}/${customPathInfo.username}/${customPathInfo.fieldName}/${customPathInfo.year}/${customPathInfo.month}/${customPathInfo.day}/${customPathInfo.documentId}/`);
                }
            }

        } catch (error) {
            console.error(`❌ Failed to upload file ${file.name}:`, error);
            throw new Error(`Upload failed for file: ${file.name} - ${getStrapiErrorMessage(error)}`);
        }
    }

    return uploadedFiles;
}

// ===== HELPER FUNCTIONS =====

/**
 * ✅ Get current username
 */
async function getCurrentUsername(baseURL: string, token: string | null): Promise<string> {
    if (!token) return FALLBACK_USERNAME;

    try {
        const response = await axios.get(`${baseURL}/api/users/me`, {
            headers: {
                'Authorization': `Bearer ${token}`,
            }
        });

        return response.data?.username || response.data?.email?.split('@')[0] || FALLBACK_USERNAME;
    } catch (error) {
        console.warn('Failed to get current username:', error);
        return FALLBACK_USERNAME;
    }
}

/**
 * ✅ Create record trong Strapi
 */
async function createRecord(
    baseURL: string,
    token: string | null,
    schema: string,
    data: any
): Promise<any> {
    const response = await axios({
        method: 'POST',
        url: `${baseURL}/api/${schema}s`, // Strapi convention: pluralize schema name
        data: { data },
        headers: {
            'Content-Type': 'application/json',
            'Authorization': token ? `Bearer ${token}` : '',
        }
    });

    return response.data?.data;
}

/**
 * ✅ Link files to existing record (Strapi v5 compatible with documentId)
 */
async function linkFilesToRecord(
    baseURL: string,
    token: string | null,
    schema: string,
    recordId: string | number,
    fieldName: string,
    files: UploadedFileInfo[]
): Promise<void> {

    try {
        // ✅ For Strapi v5, always use documentId for UPDATE operations
        let documentId = recordId;

        // If recordId looks like a regular ID, get the documentId first
        if (typeof recordId === 'number' || !recordId.toString().includes('-')) {
            console.log(`🔍 Getting documentId for ${schema} record ${recordId}`);
            const recordResponse = await axios.get(`${baseURL}/api/${schema}s/${recordId}`, {
                headers: {
                    'Authorization': token ? `Bearer ${token}` : '',
                }
            });

            documentId = recordResponse.data?.data?.documentId || recordId;
            console.log(`✅ Found documentId: ${documentId}`);
        }

        // ✅ Update record with file references using documentId
        const updateData: any = {};
        updateData[fieldName] = files.map(f => f.id);

        console.log(`🔄 Updating ${schema} record ${documentId} with ${files.length} files`);

        await axios({
            method: 'PUT',
            url: `${baseURL}/api/${schema}s/${documentId}`, // Always use documentId for UPDATE in Strapi v5
            data: { data: updateData },
            headers: {
                'Content-Type': 'application/json',
                'Authorization': token ? `Bearer ${token}` : '',
            }
        });

        console.log(`✅ Successfully linked ${files.length} files to ${schema} record ${documentId}`);

    } catch (error) {
        console.error(`❌ Failed to link files to ${schema} record:`, error);
        throw new Error(`Failed to link files to ${schema}: ${getStrapiErrorMessage(error)}`);
    }
}

/**
 * ✅ Parse universal MinIO path structure
 * Expected: {schema}/{username}/{fieldName}/{year}/{month}/{day}/{documentId}/{hash}.{ext}
 */
function parseUniversalMinioPath(url: string): PathComponents | null {
    try {
        const urlObj = new URL(url);
        const pathWithoutBucket = urlObj.pathname.replace(/^\/[^\/]+\//, ''); // Remove bucket
        const parts = pathWithoutBucket.split('/');

        if (parts.length >= 7) {
            return {
                schema: parts[0],
                username: parts[1],
                fieldName: parts[2],
                year: parts[3],
                month: parts[4],
                day: parts[5],
                documentId: parts[6],
                filename: parts[parts.length - 1] // Last part is filename
            };
        }

        console.warn(`Unexpected MinIO path format: ${pathWithoutBucket}`);
        return null;
    } catch (error) {
        console.warn('Failed to parse MinIO path:', url);
        return null;
    }
}

/**
 * ✅ Extract MinIO path from URL
 */
function extractMinioPath(url: string): string {
    try {
        const urlObj = new URL(url);
        return urlObj.pathname.substring(1); // Remove leading slash
    } catch (error) {
        console.warn('Failed to extract path from URL:', url);
        return '';
    }
}

/**
 * ✅ Extract bucket from URL
 */
function extractBucketFromUrl(url: string): string {
    try {
        const urlObj = new URL(url);
        const pathParts = urlObj.pathname.split('/').filter(Boolean);
        return pathParts[0] || DEFAULT_BUCKET;
    } catch (error) {
        console.warn('Failed to extract bucket from URL:', url);
        return DEFAULT_BUCKET;
    }
}

/**
 * ✅ Enhanced error message extraction
 */
export const getStrapiErrorMessage = (error: any): string => {
    let errorMessage = 'Có lỗi xảy ra. Vui lòng thử lại sau.';

    if (error.response?.data) {
        const { data } = error.response;

        if (data.error) {
            if (typeof data.error === 'string') {
                errorMessage = data.error;
            } else if (data.error.message) {
                errorMessage = data.error.message;

                if (data.error.details?.errors) {
                    const validationErrors = data.error.details.errors
                        .map((e: any) => {
                            const field = e.path?.join('.') || 'Unknown field';
                            return `${field}: ${e.message}`;
                        })
                        .join('; ');

                    errorMessage = `${errorMessage}. Chi tiết: ${validationErrors}`;
                }
            }
        } else if (data.message) {
            errorMessage = data.message;
        }
    } else if (error.message) {
        errorMessage = error.message;
    }

    // ✅ Localize error messages
    if (errorMessage.includes('MinIO') || errorMessage.includes('S3')) {
        errorMessage = `Lỗi lưu trữ file: ${errorMessage}`;
    }

    if (errorMessage.includes('documentId')) {
        errorMessage = `Lỗi cập nhật document (Strapi v5): ${errorMessage}`;
    }

    return errorMessage;
};

// ===== UTILITY FUNCTIONS =====

/**
 * ✅ Check MinIO connection
 */
export const checkMinioConnection = async (baseURL: string, token: string | null): Promise<boolean> => {
    try {
        const response = await axios.get(`${baseURL}/api/upload/settings`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
            }
        });

        return response.data?.provider === 'aws-s3' && response.data?.config?.endpoint;
    } catch (error) {
        console.warn('Cannot check MinIO connection:', error);
        return false;
    }
};

/**
 * ✅ Get storage statistics
 */
export const getStorageStats = async (baseURL: string, token: string | null) => {
    try {
        const response = await axios.get(`${baseURL}/api/upload/files`, {
            headers: {
                'Authorization': token ? `Bearer ${token}` : '',
            },
            params: {
                _limit: 1,
                _start: 0
            }
        });

        return {
            totalFiles: response.headers['x-total-count'] || 0,
            storageProvider: 'minio',
            pathStructure: DEFAULT_PATH_STRUCTURE
        };
    } catch (error) {
        console.warn('Cannot fetch storage stats:', error);
        return {
            totalFiles: 0,
            storageProvider: 'unknown',
            pathStructure: 'unknown'
        };
    }
};

/**
 * ✅ Generate path structure từ template
 */
export const generatePathFromStructure = (
    structure: string,
    variables: Record<string, string>
): string => {
    return structure.replace(/\{(\w+)\}/g, (match, key) => {
        return variables[key] || match;
    });
};

// ===== SCHEMA-SPECIFIC HELPERS =====

/**
 * ✅ Helper for Content table
 */
export const uploadContentFiles = (
    baseURL: string,
    token: string | null,
    files: File[],
    contentData: any,
    onProgress?: (progress: number) => void
) => {
    return createRecordWithFiles(
        baseURL,
        token,
        contentData,
        files,
        {
            schema: 'content',
            fieldName: 'files',
            customPathStructure: '{schema}/{username}/{fieldName}/{year}/{month}/{day}/{documentId}/{hash}.{ext}'
        },
        onProgress
    );
};

/**
 * ✅ Helper for Document table
 */
export const uploadDocumentFiles = (
    baseURL: string,
    token: string | null,
    files: File[],
    documentData: any,
    onProgress?: (progress: number) => void
) => {
    return createRecordWithFiles(
        baseURL,
        token,
        documentData,
        files,
        {
            schema: 'document-table',
            fieldName: 'file',
            customPathStructure: '{schema}/{username}/{fieldName}/{year}/{month}/{day}/{documentId}/{hash}.{ext}'
        },
        onProgress
    );
};

/**
 * ✅ Helper for Teacher Documents
 */
export const uploadTeacherDocument = (
    baseURL: string,
    token: string | null,
    files: File[],
    teacherDocData: any,
    onProgress?: (progress: number) => void
) => {
    return createRecordWithFiles(
        baseURL,
        token,
        teacherDocData,
        files,
        {
            schema: 'teacher-document',
            fieldName: 'file',
            customPathStructure: '{schema}/{username}/{fieldName}/{year}/{month}/{day}/{documentId}/{hash}.{ext}'
        },
        onProgress
    );
};

/**
 * ✅ Helper for Cover Images
 */
export const uploadCoverImage = (
    baseURL: string,
    token: string | null,
    file: File,
    schema: string,
    recordId: string | number,
    onProgress?: (progress: number) => void
) => {
    return uploadAndLinkToRecord(
        baseURL,
        token,
        [file],
        {
            schema,
            fieldName: 'coverImage',
            recordId,
            customPathStructure: '{schema}/{username}/cover/{year}/{month}/{day}/{documentId}/{hash}.{ext}'
        },
        onProgress
    );
};

/**
 * ✅ Helper for Violation Evidence Files
 */
export const uploadViolationEvidence = (
    baseURL: string,
    token: string | null,
    files: File[],
    violationData: any,
    onProgress?: (progress: number) => void
) => {
    return createRecordWithFiles(
        baseURL,
        token,
        violationData,
        files,
        {
            schema: 'violation',
            fieldName: 'evidenceFiles', // Hoặc 'files' tùy theo schema design
            customPathStructure: '{schema}/{username}/evidence/{year}/{month}/{day}/{documentId}/{hash}.{ext}'
        },
        onProgress
    );
};

/**
 * ✅ Helper for Certification Documents
 */
export const uploadCertificationFiles = (
    baseURL: string,
    token: string | null,
    files: File[],
    certificationData: any,
    onProgress?: (progress: number) => void
) => {
    return createRecordWithFiles(
        baseURL,
        token,
        certificationData,
        files,
        {
            schema: 'certification',
            fieldName: 'certificateFile', // Hoặc tên field phù hợp
            customPathStructure: '{schema}/{username}/certificates/{year}/{month}/{day}/{documentId}/{hash}.{ext}'
        },
        onProgress
    );
};

/**
 * ✅ Helper for License Documents
 */
export const uploadLicenseFiles = (
    baseURL: string,
    token: string | null,
    files: File[],
    licenseData: any,
    onProgress?: (progress: number) => void
) => {
    return createRecordWithFiles(
        baseURL,
        token,
        licenseData,
        files,
        {
            schema: 'license',
            fieldName: 'licenseDocument',
            customPathStructure: '{schema}/{username}/licenses/{year}/{month}/{day}/{documentId}/{hash}.{ext}'
        },
        onProgress
    );
};

/**
 * ✅ Helper for Contract Files
 */
export const uploadContractFiles = (
    baseURL: string,
    token: string | null,
    files: File[],
    contractData: any,
    onProgress?: (progress: number) => void
) => {
    return createRecordWithFiles(
        baseURL,
        token,
        contractData,
        files,
        {
            schema: 'contract',
            fieldName: 'file',
            customPathStructure: '{schema}/{username}/contracts/{year}/{month}/{day}/{documentId}/{hash}.{ext}'
        },
        onProgress
    );
};

/**
 * ✅ Helper for Legal Case Documents
 */
export const uploadLegalCaseFiles = (
    baseURL: string,
    token: string | null,
    files: File[],
    legalCaseData: any,
    onProgress?: (progress: number) => void
) => {
    return createRecordWithFiles(
        baseURL,
        token,
        legalCaseData,
        files,
        {
            schema: 'legal-case',
            fieldName: 'documents',
            customPathStructure: '{schema}/{username}/legal/{year}/{month}/{day}/{documentId}/{hash}.{ext}'
        },
        onProgress
    );
};

/**
 * ✅ Generic Helper Factory - Tạo helper cho bất kỳ schema nào
 */
export const createSchemaUploadHelper = (
    schema: string,
    fieldName: string,
    customPathSegment?: string
) => {
    return (
        baseURL: string,
        token: string | null,
        files: File[],
        recordData: any,
        onProgress?: (progress: number) => void
    ) => {
        return createRecordWithFiles(
            baseURL,
            token,
            recordData,
            files,
            {
                schema,
                fieldName,
                customPathStructure: `{schema}/{username}/${customPathSegment || fieldName}/{year}/{month}/{day}/{documentId}/{hash}.{ext}`
            },
            onProgress
        );
    };
};

// ===== DYNAMIC HELPER CREATION =====

/**
 * ✅ Tạo upload helper động cho bất kỳ table nào
 * 
 * @example
 * const uploadMyTableFiles = createUploadHelper('my-table', 'files');
 * const result = await uploadMyTableFiles(baseURL, token, files, data);
 */
export const createUploadHelper = createSchemaUploadHelper;

/**
 * ✅ Pre-configured helpers cho các table phổ biến
 */
export const SCHEMA_HELPERS = {
    // Core content tables
    content: createSchemaUploadHelper('content', 'files', 'content'),
    'document-table': createSchemaUploadHelper('document-table', 'file', 'documents'),

    // Legal & Compliance
    violation: createSchemaUploadHelper('violation', 'evidenceFiles', 'evidence'),
    certification: createSchemaUploadHelper('certification', 'certificateFile', 'certificates'),
    license: createSchemaUploadHelper('license', 'licenseDocument', 'licenses'),
    contract: createSchemaUploadHelper('contract', 'file', 'contracts'),
    'legal-case': createSchemaUploadHelper('legal-case', 'documents', 'legal'),

    // Teacher related
    'teacher-document': createSchemaUploadHelper('teacher-document', 'file', 'teacher-docs'),
    'teacher-verification': createSchemaUploadHelper('teacher-verification', 'documents', 'verification'),

    // Financial
    payment: createSchemaUploadHelper('payment', 'receipt', 'receipts'),
    revenue: createSchemaUploadHelper('revenue', 'documents', 'revenue-docs'),

    // System
    'system-backup': createSchemaUploadHelper('system-backup', 'backupFile', 'backups'),
    'audit-log': createSchemaUploadHelper('audit-log', 'attachments', 'audit'),

    // Categories & Collections
    category: createSchemaUploadHelper('category', 'coverImage', 'covers'),
    'document-collection': createSchemaUploadHelper('document-collection', 'coverImage', 'collection-covers'),
};

export default {
    // Core functions
    universalUploadToStrapi,
    uploadAndLinkToRecord,
    createRecordWithFiles,

    // Specific helpers
    uploadContentFiles,
    uploadDocumentFiles,
    uploadTeacherDocument,
    uploadCoverImage,
    uploadViolationEvidence,
    uploadCertificationFiles,
    uploadLicenseFiles,
    uploadContractFiles,
    uploadLegalCaseFiles,

    // Dynamic helpers
    createSchemaUploadHelper,
    createUploadHelper,
    SCHEMA_HELPERS,

    // Utilities
    checkMinioConnection,
    getStorageStats,
    getStrapiErrorMessage, // Re-export from strapiV5Utils
    generatePathFromStructure
};
